import React from 'react';
import './CheckoutPage.css';
import { useBooking } from '../../hooks/useBooking';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../common/Toast';
import { CreateBookingRequest, CreateBookingResponse, BookingItem, LegacyCreateBookingResponse, BookingService, VerifyPaymentRequest } from '../../services/BookingService';
import { PaymentService, RazorpayOptions, RazorpayResponse } from '../../services/PaymentService';

// Legacy Booking API Response Interface for backward compatibility
interface BookingResponse extends LegacyCreateBookingResponse {}



interface CheckoutData {
  selectedItem?: any;
  selectedDate: string;
  quantity: number;
  quote?: any; // New single quote object from API
  quotes?: any[]; // Legacy support
  startDate?: string;
  endDate?: string;
  startTime?: string; // Added - actual time used in quote API
  endTime?: string;   // Added - actual time used in quote API
  timeSlot?: string;
  selectedSeats?: any[];
  bookingType?: 'Daily' | 'Monthly';
  meetingRoom?: {
    id: string;
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
  };
}

// Section ID to room name mapping
const SECTION_NAMES: Record<string, string> = {
  'OPEN_3SEATER_1': 'Open Desk - 3 Seater',
  'OPEN_3SEATER_2': 'Open Desk - 3 Seater', 
  'OPEN_2SEATER': 'Open Desk - 2 Seater',
  'OPEN_4SEATER': 'Open Desk - 4 Seater',
  'TUNGA': 'Tunga',
  'CAUVERY': 'Cauvery',
  'BHIMA': 'Bhima',
  'KRISHNA': 'Krishna',
  'SHARAVATHI': 'Sharavathi',
  'BHADRA': 'Bhadra',
  'VARADA': 'Varada',
  'SANGAMA': 'Sangama'
};

interface CheckoutPageProps {
  onBack: () => void;
  checkoutData?: CheckoutData;
  onPurchaseComplete: (transactionData: any) => void;
}

export const CheckoutPage: React.FC<CheckoutPageProps> = ({ onBack, checkoutData, onPurchaseComplete }) => {
  const [promoExpanded, setPromoExpanded] = React.useState(false);
  const { createBooking, isLoading: isBookingLoading, error: bookingError, clearError } = useBooking();
  const { toasts, removeToast, showError, showSuccess, showWarning } = useToast();
  const [formData, setFormData] = React.useState({
    fullName: '',
    email: '',
    phone: '',
    gstNumber: '',
    address: '',
    city: '',
    region: '',
    postCode: '',
    termsAccepted: false
  });
  const [errors, setErrors] = React.useState<{[key: string]: string}>({});
  
  // Debug environment variables on component mount
  React.useEffect(() => {
    console.log('=== ENVIRONMENT DEBUG ON MOUNT ===');
    console.log('All process.env:', process.env);
    console.log('REACT_APP_RAZORPAY_KEY_ID:', process.env.REACT_APP_RAZORPAY_KEY_ID);
    console.log('REACT_APP_ENVIRONMENT:', process.env.REACT_APP_ENVIRONMENT);
    console.log('NODE_ENV:', process.env.NODE_ENV);
    console.log('================================');
  }, []);
  
  // Handle Razorpay payment
  const initiateRazorpayPayment = async (bookingResponse: CreateBookingResponse) => {
    console.log('=== RAZORPAY DEBUG INFO ===');
    console.log('Booking Response:', bookingResponse);
    console.log('Razorpay Order ID:', bookingResponse.razorpay_order_id);
    console.log('Environment Variables:', {
      REACT_APP_RAZORPAY_KEY_ID: process.env.REACT_APP_RAZORPAY_KEY_ID,
      REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT
    });
    
    const customerAddress = `${formData.address}, ${formData.city}, ${formData.region}, ${formData.postCode}`;
    
    // Get Razorpay key from environment
    const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
    
    console.log('Razorpay Key from env:', razorpayKey);
    console.log('Raw env var:', process.env.REACT_APP_RAZORPAY_KEY_ID);
    
    if (!razorpayKey) {
      console.error('❌ Razorpay key is missing from environment variables');
      alert('Payment configuration error. Please contact support.');
      return;
    }
    
    if (!bookingResponse.razorpay_order_id) {
      console.error('❌ Razorpay order ID is missing from booking response');
      alert('Payment order creation failed. Please contact support.');
      return;
    }
    
    console.log('✅ About to create Razorpay options with:');
    console.log('Key:', razorpayKey);
    console.log('Order ID:', bookingResponse.razorpay_order_id);
    console.log('Amount:', bookingResponse.total_amount);
    
    const options: RazorpayOptions = {
      key: razorpayKey,
      amount: parseInt(bookingResponse.total_amount) || 0,
      currency: bookingResponse.currency,
      name: 'Co-Work@Level3',
      description: `${bookingResponse.booking_code} - ${bookingResponse.user_name}`,
      order_id: bookingResponse.razorpay_order_id,
      handler: async (response: RazorpayResponse) => {
        // Verify payment with backend
        const verificationData: VerifyPaymentRequest = {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature
        };
        
        try {
          console.log('=== PAYMENT VERIFICATION DEBUG ===');
          console.log('Verification data being sent:', verificationData);
          console.log('API endpoint:', '/api/booking-orders/verify-order-payment');
          
          const verificationResponse = await BookingService.verifyPayment(verificationData);
          
          console.log('Verification response:', verificationResponse);
          
          // Check for successful payment verification
          // API returns order_status: 'confirmed' instead of payment_status: 'success'
          const isPaymentSuccessful = verificationResponse && 
            (verificationResponse.payment_status === 'success' || 
             verificationResponse.order_status === 'confirmed');
          
          console.log('Payment successful check:', isPaymentSuccessful);
          
          if (isPaymentSuccessful) {
            console.log('✅ Payment verified successfully, proceeding to success page');
            
            // Payment verified successfully
            const transactionData = {
              transactionId: bookingResponse.id,
              paymentId: response.razorpay_payment_id,
              bookingCode: verificationResponse.booking_code,
              customerInfo: {
                fullName: formData.fullName,
                email: formData.email,
                phone: formData.phone
              },
              workspaceInfo: {
                title: workspaceTitle,
                location: workspaceLocation,
                price: parseFloat(bookingResponse.base_amount)
              },
              billingInfo: {
                startDate: checkoutData?.startDate || new Date().toLocaleDateString(),
                endDate: checkoutData?.endDate || new Date().toLocaleDateString(),
                amount: parseFloat(bookingResponse.base_amount),
                tax: parseFloat(bookingResponse.cgst_amount) + parseFloat(bookingResponse.sgst_amount),
                total: parseFloat(bookingResponse.total_amount)
              },
              paymentDate: new Date().toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }),
              paymentStatus: verificationResponse.order_status || 'confirmed'
            };
            
            console.log('✅ Calling onPurchaseComplete with:', transactionData);
            onPurchaseComplete(transactionData);
          } else {
            console.error('❌ Payment verification failed - invalid response');
            alert('Payment verification failed. Please contact support.');
          }
        } catch (error) {
          alert(`Payment verification failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      },
      prefill: {
        name: formData.fullName,
        email: formData.email,
        contact: formData.phone
      },
      notes: {
        address: customerAddress
      },
      theme: {
        color: '#3399cc'
      }
    };
    
    try {
      console.log('✅ Calling PaymentService.initiateRazorpayPayment with options:', options);
      await PaymentService.initiateRazorpayPayment(options);
      console.log('✅ Payment initiated successfully - waiting for Razorpay response');
    } catch (error) {
      console.error('❌ CHECKPOINT 1: Caught error in CheckoutPage:', error);
      const errorMessage = error instanceof Error ? error.message : 'Payment failed';
      console.error('❌ CHECKPOINT 2: Error message extracted:', errorMessage);
      
      // Show user-friendly error message using toast
      console.log('❌ CHECKPOINT 3: Checking error type...');
      if (errorMessage.includes('cancelled') || errorMessage.includes('closed')) {
        console.log('❌ CHECKPOINT 4: Detected cancellation - calling showError with toast');
        showError('❌ Payment Cancelled\n' + errorMessage, 0);
        console.log('❌ CHECKPOINT 5: showError called for cancellation');
      } else if (errorMessage.includes('failed')) {
        console.log('❌ CHECKPOINT 4: Detected failure - calling showError with toast');
        showError('❌ Payment Failed\n' + errorMessage, 0);
        console.log('❌ CHECKPOINT 5: showError called for failure');
      } else {
        console.log('❌ CHECKPOINT 4: Generic error - calling showError with toast');
        showError('❌ Error\n' + errorMessage, 0);
        console.log('❌ CHECKPOINT 5: showError called for generic error');
      }
    }
  };
  
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation (10 digits)
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
    
    // GST Number validation (optional but if provided must be 15 alphanumeric characters)
    if (formData.gstNumber.trim()) {
      if (!/^[a-zA-Z0-9]{15}$/.test(formData.gstNumber.trim())) {
        newErrors.gstNumber = 'GST Number must be exactly 15 alphanumeric characters with no special characters';
      }
    }
    
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.region.trim()) newErrors.region = 'Region is required';
    if (!formData.postCode.trim()) newErrors.postCode = 'Post code is required';
    if (!formData.termsAccepted) newErrors.terms = 'You must agree to the terms and conditions';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  const isFormFilled = formData.fullName && formData.email && formData.phone && 
                      formData.address && formData.city && formData.region && 
                      formData.postCode && formData.termsAccepted;
  
  // Map time slot to API format - used only for time extraction, not booking_type
  const mapTimeSlotToAPI = (timeSlot: string): { booking_type: string; start_time: string; end_time: string } => {
    const slotMapping: Record<string, { booking_type: string; start_time: string; end_time: string }> = {
      '9:30 AM - 5:30 PM': { booking_type: 'daily', start_time: '09:30:00', end_time: '17:30:00' },
      '5:30 PM - 1:30 AM': { booking_type: 'evening', start_time: '17:30:00', end_time: '01:30:00' }, 
      '1:30 AM - 9:30 AM': { booking_type: 'night', start_time: '01:30:00', end_time: '09:30:00' }
    };
    return slotMapping[timeSlot] || { booking_type: 'daily', start_time: '09:00:00', end_time: '18:00:00' };
  };
  
  // Get booking parameters from checkoutData (should match quote parameters)
  const getBookingParameters = () => {
    // Use the same date/time format as used in quote API
    const startDate = checkoutData?.startDate || new Date().toISOString().split('T')[0];
    const endDate = checkoutData?.endDate || new Date().toISOString().split('T')[0];
    
    // Extract booking_type from quote data (most accurate since it was used to generate the quote)
    let bookingType = 'daily'; // fallback
    
    if (checkoutData?.quote?.booking_type) {
      // Use the exact booking_type from the quote response
      bookingType = checkoutData.quote.booking_type;
    } else if (checkoutData?.bookingType) {
      // Convert UI booking type to API format
      bookingType = checkoutData.bookingType === 'Monthly' ? 'monthly' : 'daily';
    }
    
    // Use the ACTUAL start_time and end_time that were used in the quote API call
    let startTime = checkoutData?.startTime || '09:00:00'; // Use actual time from SeatMap
    let endTime = checkoutData?.endTime || '18:00:00';     // Use actual time from SeatMap
    
    // Only use timeSlot mapping as absolute fallback if no actual times are available
    if (!checkoutData?.startTime && !checkoutData?.endTime && checkoutData?.timeSlot) {
      const timeSlotInfo = mapTimeSlotToAPI(checkoutData.timeSlot);
      startTime = timeSlotInfo.start_time;
      endTime = timeSlotInfo.end_time;
    }
    
    return {
      startDate,
      endDate,
      startTime, // Now uses the EXACT same time as quote API
      endTime,   // Now uses the EXACT same time as quote API
      bookingType // This is now dynamic based on quote or user selection
    };
  };
  
  // Get workspace display info from selected seats
  const getWorkspaceInfo = () => {
    if (!checkoutData?.selectedSeats || checkoutData.selectedSeats.length === 0) {
      return { title: 'Premium Workspace', location: 'Co-work@Level 3, Bengaluru' };
    }
    
    const selectedSeats = checkoutData.selectedSeats;
    const deskCodes = selectedSeats.map((seat: any) => seat.code).filter(Boolean);
    const roomNamesSet = new Set(selectedSeats.map((seat: any) => SECTION_NAMES[seat.sectionId]).filter(Boolean));
    const roomNames = Array.from(roomNamesSet);
    
    const title = deskCodes.length > 0 ? deskCodes.join(', ') : 'Selected Desks';
    const location = roomNames.length > 0 ? roomNames.join(', ') : 'Co-work@Level 3, Bengaluru';
    
    return { title, location };
  };
  
  // Helper function to format date and time
  const formatDateTime = (date: string, time?: string) => {
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    
    if (time) {
      const [hours, minutes] = time.split(':');
      const timeObj = new Date();
      timeObj.setHours(parseInt(hours), parseInt(minutes));
      const formattedTime = timeObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${formattedDate} ${formattedTime}`;
    }
    
    return formattedDate;
  };

  // Calculate duration for desk and meeting room
  const calculateDuration = (startDate: string, endDate: string, startTime?: string, endTime?: string, bookingType?: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (bookingType === 'hourly') {
      // Calculate hours for meeting room
      if (startTime && endTime) {
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        
        start.setHours(startHour, startMin, 0, 0);
        end.setHours(endHour, endMin, 0, 0);
        
        const diffInMs = end.getTime() - start.getTime();
        const hours = Math.ceil(diffInMs / (1000 * 60 * 60));
        return { value: hours, unit: hours === 1 ? 'hour' : 'hours' };
      }
    } else {
      // Calculate days for desk
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const duration = days === 0 ? 1 : days;
      return { value: duration, unit: duration === 1 ? 'day' : 'days' };
    }
    
    return { value: 1, unit: 'day' };
  };

  // Parse quote items to separate desk and meeting room data
  const parseQuoteItems = () => {
    if (!checkoutData?.quote?.items) {
      return { deskItem: null, meetingRoomItem: null };
    }
    
    const items = checkoutData.quote.items;
    const deskItem = items.find((item: any) => item.resource_type === 'DESK');
    const meetingRoomItem = items.find((item: any) => item.resource_type === 'MEETING_ROOM');
    
    return { deskItem, meetingRoomItem };
  };
  
  const { deskItem, meetingRoomItem } = parseQuoteItems();
  
  // Calculate total pricing from quote
  const totalPricing = {
    basePrice: parseFloat(checkoutData?.quote?.base_price || '0'),
    cgstAmount: parseFloat(checkoutData?.quote?.cgst_amount || '0'),
    sgstAmount: parseFloat(checkoutData?.quote?.sgst_amount || '0'),
    totalTax: parseFloat(checkoutData?.quote?.total_tax || '0'),
    totalPrice: parseFloat(checkoutData?.quote?.total_price || '0')
  };
  
  const { title: workspaceTitle, location: workspaceLocation } = getWorkspaceInfo();
  
  // Calculate duration and rate type
  const getDurationInfo = () => {
    if (!checkoutData?.startDate || !checkoutData?.endDate) {
      return { duration: 1, rateType: 'daily rate applied' };
    }
    
    const start = new Date(checkoutData.startDate);
    const end = new Date(checkoutData.endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // If start and end dates are the same, it's 1 day
    const duration = diffDays === 0 ? 1 : diffDays;
    
    const isDaily = duration <= 30;
    return {
      duration,
      rateType: isDaily ? 'daily rate applied' : 'monthly rate applied'
    };
  };
  
  const { duration, rateType } = getDurationInfo();
  return (
    <div className="checkout-page">
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <div className="checkout-header">
        <button onClick={onBack} className="back-button">
          <span>← Back to Booking</span>
        </button>
      </div>
      
      <div className="checkout-container">
        <div className="checkout-layout">
          {/* Left Column - Form */}
          <div className="checkout-form-column">
            {/* Contact Details */}
            <div className="form-section">
              <h2>Contact Details</h2>
              <div className="form-group">
                <input 
                  type="text" 
                  placeholder="Full Name" 
                  className={`form-input ${errors.fullName ? 'error' : ''}`}
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  required 
                />
                {errors.fullName && <p className="error-text">{errors.fullName}</p>}
              </div>
              <div className="form-group">
                <input 
                  type="email" 
                  placeholder="Email" 
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required 
                />
                {errors.email && <p className="error-text">{errors.email}</p>}
                <p className="helper-text">We'll send booking confirmation to this email</p>
              </div>
              <div className="form-group">
                <input 
                  type="tel" 
                  placeholder="Phone Number" 
                  className={`form-input ${errors.phone ? 'error' : ''}`}
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required 
                />
                {errors.phone && <p className="error-text">{errors.phone}</p>}
              </div>
            </div>
            
            <div className="form-divider"></div>
            
            {/* Billing Details */}
            <div className="form-section">
              <h2>Billing Details</h2>
              <div className="form-group">
                  <input 
                    type="text" 
                    placeholder="GST Number" 
                    className={`form-input ${errors.gstNumber ? 'error' : ''}`}
                    value={formData.gstNumber}
                    onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                  />
                  {errors.gstNumber && <p className="error-text">{errors.gstNumber}</p>}
                  <p className="helper-text">Optional: 15 alphanumeric characters</p>
                </div>
              <div className="address-grid">
                <div className="form-group">
                  <input 
                    type="text" 
                    placeholder="Street Address" 
                    className={`form-input ${errors.address ? 'error' : ''}`}
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                  />
                  {errors.address && <p className="error-text">{errors.address}</p>}
                </div>
                <div className="form-group">
                  <input 
                    type="text" 
                    placeholder="City" 
                    className={`form-input ${errors.city ? 'error' : ''}`}
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                  {errors.city && <p className="error-text">{errors.city}</p>}
                </div>
                <div className="form-group">
                  <input 
                    type="text" 
                    placeholder="Region" 
                    className={`form-input ${errors.region ? 'error' : ''}`}
                    value={formData.region}
                    onChange={(e) => handleInputChange('region', e.target.value)}
                  />
                  {errors.region && <p className="error-text">{errors.region}</p>}
                </div>
                <div className="form-group">
                  <input 
                    type="text" 
                    placeholder="Post Code" 
                    className={`form-input ${errors.postCode ? 'error' : ''}`}
                    value={formData.postCode}
                    onChange={(e) => handleInputChange('postCode', e.target.value)}
                  />
                  {errors.postCode && <p className="error-text">{errors.postCode}</p>}
                </div>
              </div>
              <div className="form-group">
                <select className="form-input">
                  <option>India</option>
                </select>
              </div>
            </div>
            
            <div className="form-divider"></div>
            
            {/* Terms & Conditions */}
            <div className="form-section">
              <h2>Terms & Conditions</h2>
              <label className="checkbox-row">
                <input 
                  type="checkbox" 
                  checked={formData.termsAccepted}
                  onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                  required 
                />
                <span>I agree to the <a href="/terms/" target="_blank" rel="noopener noreferrer" className="terms-link">Terms of Service</a> and acknowledge that this booking is non-refundable in accordance with the Terms of Service.</span>
              </label>
              {errors.terms && <p className="error-text">{errors.terms}</p>}
            </div>            
            <div className="form-divider"></div>
            
            {/* Primary Action */}
            <div className="primary-action">
              {bookingError && (
                <div className="text-red-500 text-sm mb-4 p-3 bg-red-50 border border-red-200 rounded">
                  {bookingError}
                </div>
              )}
              <button 
                className="continue-button" 
                disabled={!isFormFilled || isBookingLoading}
                onClick={async () => {
                  if (!validateForm()) {
                    return;
                  }
                  
                  clearError();
                  
                  // Prepare booking data using the new API format
                  const items: BookingItem[] = [];
                  
                  // Add desk item if seats are selected
                  const selectedSeats = checkoutData?.selectedSeats || [];
                  if (selectedSeats.length > 0) {
                    const bookingParams = getBookingParameters();
                    const deskCodes = selectedSeats.map((seat: any) => seat.code || seat.id).filter(Boolean);
                    
                    items.push({
                      resource_type: 'DESK',
                      resource_id: deskCodes,
                      booking_type: bookingParams.bookingType as 'daily' | 'monthly',
                      start_date: bookingParams.startDate,
                      start_time: bookingParams.startTime,
                      end_date: bookingParams.endDate,
                      end_time: bookingParams.endTime
                    });
                  }
                  
                  // Add meeting room item if meeting room is selected
                  if (checkoutData?.meetingRoom) {
                    const meetingRoom = checkoutData.meetingRoom;
                    items.push({
                      resource_type: 'MEETING_ROOM',
                      resource_id: meetingRoom.id,
                      booking_type: 'hourly',
                      start_date: meetingRoom.startDate,
                      start_time: meetingRoom.startTime,
                      end_date: meetingRoom.endDate,
                      end_time: meetingRoom.endTime
                    });
                  }
                  
                  if (items.length === 0) {
                    alert('No items selected for booking');
                    return;
                  }
                  
                  const bookingData: CreateBookingRequest = {
                    mobile_number: formData.phone,
                    name: formData.fullName,
                    email: formData.email,
                    address: `${formData.address}, ${formData.city}, ${formData.region}, ${formData.postCode}`,
                    gstNumber: formData.gstNumber,
                    items: items
                  };
                  
                  // Create booking
                  const bookingResponse = await createBooking(bookingData);
                  
                  if (bookingResponse) {
                    // Check if payment is required (both razorpay_order_id and id exist)
                    if (bookingResponse.razorpay_order_id && bookingResponse.id) {
                      // Initiate Razorpay payment
                      await initiateRazorpayPayment(bookingResponse);
                    } else {
                      // No payment required, proceed directly
                      const transactionData = {
                        transactionId: bookingResponse.id || 'TXN' + Date.now(),
                        customerInfo: {
                          fullName: formData.fullName,
                          email: formData.email,
                          phone: formData.phone
                        },
                        workspaceInfo: {
                          title: workspaceTitle,
                          location: workspaceLocation,
                          price: totalPricing.basePrice
                        },
                        billingInfo: {
                          startDate: checkoutData?.startDate || new Date().toLocaleDateString(),
                          endDate: checkoutData?.endDate || new Date().toLocaleDateString(),
                          amount: totalPricing.basePrice,
                          tax: totalPricing.totalTax,
                          total: totalPricing.totalPrice
                        },
                        paymentDate: new Date().toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })
                      };
                      
                      onPurchaseComplete(transactionData);
                    }
                  }
                }}
              >
                <span>{isBookingLoading ? 'Creating Booking...' : 'Proceed to Payment'}</span>
              </button>
            </div>
          </div>
          
          {/* Right Column - Summary */}
          <div className="checkout-summary-column">
            <div className="summary-sticky">
              {/* Summary Card */}
              <div className="summary-card">
                {/* Workspace Info */}
                {/* <div className="workspace-info">
                  <h3>{workspaceTitle}, {workspaceLocation}</h3>
                  <p className="workspace-address">{"Co-work@Level 3, Bengaluru"}</p>
                </div> */}
                
                {/* Price Overview
                <div className="price-overview">
                  {checkoutData?.quote && (
                    <span className="price">
                      ₹{checkoutData.quote.base_price?.toLocaleString()} / {checkoutData?.bookingType === 'Monthly' ? 'month' : 'day'}
                    </span>
                  )}
                </div> */}
                
                {/* <div className="summary-divider"></div> */}
                
                {/* Subscription Details
                <div className="subscription-details">
                  <div className="detail-row">
                    <span>Start Date</span>
                    <span>{checkoutData?.startDate || new Date().toLocaleDateString()}</span>
                  </div>
                  <div className="detail-row">
                    <span>End Date</span>
                    <span>{checkoutData?.endDate || new Date().toLocaleDateString()}</span>
                  </div>
                   <div className="detail-row">
                    <span>Time Slot</span>
                    <span>{checkoutData?.timeSlot || new Date().toLocaleDateString()}</span>
                  </div>
                </div> */}
                
                <div className="summary-divider"></div>
                
                {/* Enhanced Price Breakdown */}
                <div className="price-breakdown">
                  <div className="booking-details-header">
                    <h4 style={{margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600'}}>Booking Details</h4>
                    <p style={{margin: '0 0 16px 0', fontSize: '14px', color: '#666'}}>Co-work@Level 3, Bengaluru</p>
                  </div>
                  
                  {/* <div style={{marginBottom: '16px', fontSize: '14px', fontWeight: '600', color: '#333'}}>
                    {checkoutData?.quote && (
                    <span className="price">
                      ₹{checkoutData.quote.base_price?.toLocaleString()} / {checkoutData?.bookingType === 'Monthly' ? 'month' : 'day'}
                    </span>
                    )}
                  </div> */}
                  
                  <div style={{borderBottom: '1px solid #eee', marginBottom: '16px'}}></div>
                  
                  {/* Desk Details */}
                  {deskItem && (
                    <div style={{marginBottom: '20px'}}>
                      <div className="detail-row" style={{fontWeight: '600', marginBottom: '8px'}}>
                        <span>Desk - {deskItem.resource_ids.join(', ')}</span>
                        <span></span>
                      </div>
                      <div className="detail-row" style={{fontSize: '13px', color: '#666'}}>
                        <span>Start Date</span>
                        <span>{formatDateTime(deskItem.start_date)}</span>
                      </div>
                      <div className="detail-row" style={{fontSize: '13px', color: '#666'}}>
                        <span>End Date</span>
                        <span>{formatDateTime(deskItem.end_date)}</span>
                      </div>
                      <div className="detail-row" style={{fontSize: '13px', marginTop: '8px'}}>
                        <span>Base Price</span>
                        <span>₹{parseFloat(deskItem.base_price).toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Meeting Room Details */}
                  {meetingRoomItem && (
                    <div style={{marginBottom: '20px'}}>
                      <div className="detail-row" style={{fontWeight: '600', marginBottom: '8px'}}>
                        <span>Meeting Room - {meetingRoomItem.resource_ids.join(', ')}</span>
                        <span></span>
                      </div>
                      <div className="detail-row" style={{fontSize: '13px', color: '#666'}}>
                        <span>Start Date and Time</span>
                        <span>{formatDateTime(meetingRoomItem.start_date, meetingRoomItem.start_time)}</span>
                      </div>
                      <div className="detail-row" style={{fontSize: '13px', color: '#666'}}>
                        <span>End Date and Time</span>
                        <span>{formatDateTime(meetingRoomItem.end_date, meetingRoomItem.end_time)}</span>
                      </div>
                      <div className="detail-row" style={{fontSize: '13px', marginTop: '8px'}}>
                        <span>Base Price</span>
                        <span>₹{parseFloat(meetingRoomItem.base_price).toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                  
                  <div style={{borderBottom: '1px solid #eee', marginBottom: '16px'}}></div>
                  
                  {/* Total Base Price */}
                  <div className="detail-row" style={{fontWeight: '600', marginBottom: '12px'}}>
                    <span>Base Price</span>
                    <span>₹{totalPricing.basePrice.toLocaleString()}</span>
                  </div>
                  
                  {/* GST Breakdown */}
                  <div className="detail-row" style={{marginBottom: '4px'}}>
                    <span>GST (18%):</span>
                    <span></span>
                  </div>
                  <div className="detail-row" style={{paddingLeft: '20px', fontSize: '13px', color: '#666'}}>
                    <span>CGST (9%)</span>
                    <span>₹{totalPricing.cgstAmount.toLocaleString()}</span>
                  </div>
                  <div className="detail-row" style={{paddingLeft: '20px', fontSize: '13px', color: '#666', marginBottom: '16px'}}>
                    <span>SGST (9%)</span>
                    <span>₹{totalPricing.sgstAmount.toLocaleString()}</span>
                  </div>
                  
                  {/* <div style={{borderBottom: '2px solid #333', marginBottom: '16px'}}></div> */}
                  
                  {/* Total Amount */}
                  <div className="breakdown-row total" style={{fontSize: '16px', fontWeight: '700', marginBottom: '16px'}}>
                    <span>Total Amount (incl. GST):</span>
                    <span>₹{totalPricing.totalPrice.toLocaleString()}</span>
                  </div>
                  
                  {/* Duration Summary */}
                  <div style={{fontSize: '13px', color: '#666', lineHeight: '1.4'}}>
                    {deskItem && (
                      <div>
                        {(() => {
                          const duration = calculateDuration(deskItem.start_date, deskItem.end_date, deskItem.start_time, deskItem.end_time, deskItem.booking_type);
                          return `${duration.value} ${duration.unit} applied for desk`;
                        })()} 
                      </div>
                    )}
                    {meetingRoomItem && (
                      <div>
                        {(() => {
                          const duration = calculateDuration(meetingRoomItem.start_date, meetingRoomItem.end_date, meetingRoomItem.start_time, meetingRoomItem.end_time, meetingRoomItem.booking_type);
                          return `${duration.value} ${duration.unit} applied for meeting room`;
                        })()} 
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Promo Code Card
              <div className="promo-card">
                <div className="promo-header" onClick={() => setPromoExpanded(!promoExpanded)}>
                  <span>I have a promo code</span>
                  <button className="expand-btn">{promoExpanded ? '-' : '+'}</button>
                </div>
                {promoExpanded && (
                  <div className="promo-input-row">
                    <input type="text" placeholder="Enter promo code" className="promo-input" />
                    <button className="apply-btn">Apply</button>
                  </div>
                )}
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};